import {
  Ban,
  Bell,
  Circle,
  CircleCheck,
  CircleX,
  GitPullRequestArrow,
  Globe,
  RefreshCw,
  Share2,
  Zap,
} from "lucide-react";
import type { ReactElement } from "react";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { useInboxAvailability } from "~/notifications/data-source/use-inbox-availability";
import { useNotificationPolling } from "~/notifications/data-source/use-notification-polling";
import { useNotificationUrl } from "~/notifications/feature/use-notification-url";
import {
  getNotificationToastDescription,
  getNotificationToastTitle,
  type Notification,
  type ReceivedResourceEvent,
  type WorkflowLogCompletedNotification,
} from "~/notifications/model/notification";

// 30-second lookback window: suppresses historical events from before this session
// while still catching notifications that arrived just before the page loaded
// (e.g. share-then-reload) and absorbing normal server/client clock skew.
const SESSION_START = new Date(Date.now() - 30_000);

const INDIVIDUAL_THRESHOLD = 3;
const EXPAND_CHUNK = 3;
const INBOX_ROUTE = "/notifications";
const INDIVIDUAL_TOAST_DURATION_MS = 10_000;
const SUMMARY_TOAST_DURATION_MS = 15_000;

export function useNotificationToasts() {
  const { hasInbox } = useInboxAvailability();
  const pollingQuery = useNotificationPolling({ enabled: hasInbox === true });
  const navigate = useNavigate();
  const pollingData = pollingQuery.data;

  const getUrl = useNotificationUrl();

  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const getUrlRef = useRef(getUrl);
  getUrlRef.current = getUrl;

  const previousIsErrorRef = useRef(false);
  useEffect(() => {
    if (pollingQuery.isError && !previousIsErrorRef.current) {
      console.error("[notifications] polling failed", pollingQuery.error);
    }
    previousIsErrorRef.current = pollingQuery.isError;
  }, [pollingQuery.isError, pollingQuery.error]);

  useEffect(() => {
    if (!pollingData) return;

    const fresh = pollingData.filter((n) => n.createdAt >= SESSION_START);
    if (fresh.length === 0) return;

    if (fresh.length <= INDIVIDUAL_THRESHOLD) {
      for (const notification of fresh) fireIndividual(notification);
      return;
    }

    fireSummary(fresh);

    function fireIndividual(notification: Notification) {
      const url = getUrlRef.current(notification);
      toast(getNotificationToastTitle(notification), {
        id: `notif-${notification.id}`,
        description: getNotificationToastDescription(notification),
        icon: getNotificationIcon(notification),
        duration: INDIVIDUAL_TOAST_DURATION_MS,
        action: url
          ? {
              label: "View",
              onClick: () => {
                const currentUrl = getUrlRef.current(notification);
                if (currentUrl) navigateRef.current(currentUrl);
              },
            }
          : undefined,
      });
    }

    function fireSummary(batch: Notification[]) {
      const id = `notif-summary-${batch[0].id}`;
      toast(`${batch.length} new notification${batch.length === 1 ? "" : "s"}`, {
        id,
        icon: <Bell className="size-4" aria-hidden />,
        duration: SUMMARY_TOAST_DURATION_MS,
        action: {
          label: "View inbox",
          onClick: () => navigateRef.current(INBOX_ROUTE),
        },
        cancel: {
          label: "Expand",
          onClick: () => {
            toast.dismiss(id);
            expandBatch(batch);
          },
        },
      });
    }

    function expandBatch(batch: Notification[]) {
      const chunk = batch.slice(0, EXPAND_CHUNK);
      const remaining = batch.slice(EXPAND_CHUNK);

      for (const notification of chunk) fireIndividual(notification);

      if (remaining.length > INDIVIDUAL_THRESHOLD) {
        fireSummary(remaining);
      } else {
        for (const notification of remaining) fireIndividual(notification);
      }
    }
  }, [pollingData]);
}

function getNotificationIcon(notification: Notification): ReactElement {
  switch (notification.type) {
    case "share":
      return <Share2 className="size-4 text-blue-500" aria-hidden />;
    case "received-resource":
      return getReceivedResourceIcon(notification.payload.event);
    case "changeRequest":
      return <GitPullRequestArrow className="text-info size-4" aria-hidden />;
    case "workflowLog":
      return getWorkflowOutcomeIcon(notification);
    case "job":
      return notification.payload.jobStatus === "completed" ? (
        <CircleCheck className="size-4 text-green-500" aria-hidden />
      ) : (
        <Zap className="size-4 text-red-500" aria-hidden />
      );
  }
}

function getReceivedResourceIcon(event: ReceivedResourceEvent) {
  switch (event) {
    case "shared":
      return <Globe className="size-4 text-blue-500" aria-hidden />;
    case "updated":
      return <RefreshCw className="size-4 text-blue-500" aria-hidden />;
    case "revoked":
      return <Ban className="text-muted-foreground size-4" aria-hidden />;
  }
}

function getWorkflowOutcomeIcon(notification: WorkflowLogCompletedNotification): ReactElement {
  switch (notification.payload.outcome) {
    case "success":
      return <CircleCheck className="text-success size-4" aria-hidden />;
    case "failure":
      return <CircleX className="text-destructive size-4" aria-hidden />;
    case "unspecified":
      return <Circle className="text-muted-foreground size-4" aria-hidden />;
  }
}
